// File List component (table view)
import { useState, useMemo, useEffect, useRef } from 'react';
import { FileAudio, Download, Star, ChevronDown, ChevronRight, Play, Pause, ArrowUp, ArrowDown, Folder as FolderIcon } from 'lucide-react';
import { ManageVersionsButton } from './ManageVersionsButton';
import { AudioFile, Folder } from '@types';
import { Tag, StatusBadge } from '@components/ui';
import { FileActions } from '@components/file/FileActions';
import { ShareButton } from '@components/file/ShareButton';
import { RenameModal } from '@components/file/RenameModal';
import { useFileStore } from '@stores/useFileStore';
import { useAudioStore } from '@stores/useAudioStore';
import { useToast } from '@components/ui';
import { formatBytes, formatDuration, formatDate, formatBPM } from '@utils/format';
import { FILE_TYPE_COLORS } from '@config/constants';
import { cn } from '@utils/classNames';
import { type FileGroup, getCollaboratorLabelForGroup, getFileCreatedAtMs } from '@utils/fileGrouping';

interface FileListProps {
  fileGroups: FileGroup[];
  onFileClick?: (file: AudioFile) => void;
  onFolderClick?: (folder: Folder) => void;
  onFileUpdate?: () => void;
  onDragStart?: (file: AudioFile) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  getFolderPath?: (folderId: string | undefined) => string;
  selectedFileIds?: string[];
  onToggleSelect?: (fileId: string) => void;
  onFilePlay?: (file: AudioFile) => void; // Optional custom play handler
  hideActions?: boolean; // Hide actions column
  localPlayingFileId?: string | null; // Local playing file ID (when using local player)
  localIsPlaying?: boolean; // Local playing state (when using local player)
  /** Open this group on the Versions page (navigate with state.openGroupId). */
  onViewGroupInVersions?: (groupId: string) => void;
}

export const FileList: React.FC<FileListProps> = ({ 
  fileGroups,
  onFileClick,
  onFolderClick,
  onFileUpdate,
  onDragStart,
  onDragEnd,
  isDragging: _isDragging, // Keep for API compatibility but use draggedFile state instead
  selectedFileIds = [],
  onToggleSelect,
  onFilePlay,
  hideActions = false,
  localPlayingFileId,
  localIsPlaying,
  onViewGroupInVersions,
}) => {
  const [renameFile, setRenameFile] = useState<AudioFile | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingBpm, setEditingBpm] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [tempBpm, setTempBpm] = useState<string>('');
  const [tempKey, setTempKey] = useState<string>('');
  const [draggedFile, setDraggedFile] = useState<AudioFile | null>(null);
  const [dragOverFile, setDragOverFile] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string | null>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const { getBlobUrl, updateFile, toggleFileStar, files: allFiles } = useFileStore();
  const [starringIds, setStarringIds] = useState<Set<string>>(() => new Set());

  const resolveFile = (file: AudioFile) => allFiles.find((f) => f.id === file.id) ?? file;
  const { play, currentFile, isPlaying, stop } = useAudioStore();
  const { success, error: showError } = useToast();

  // Reset expanded groups when fileGroups change (e.g., when filters change)
  // Use a ref to track if we're clicking on a version to prevent closing groups
  const isClickingVersion = useRef(false);
  const previousGroupIds = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    // Don't reset if we're clicking on a version
    if (isClickingVersion.current) {
      isClickingVersion.current = false;
      // Update previous group IDs to current ones
      previousGroupIds.current = new Set(fileGroups.map(g => g.id));
      return;
    }
    
    // Only reset if groups were actually added/removed (not just data updated)
    const currentGroupIds = new Set(fileGroups.map(g => g.id));
    const groupsChanged = 
      currentGroupIds.size !== previousGroupIds.current.size ||
      Array.from(currentGroupIds).some(id => !previousGroupIds.current.has(id)) ||
      Array.from(previousGroupIds.current).some(id => !currentGroupIds.has(id));
    
    if (groupsChanged) {
      // Keep currently expanded groups that still exist
      const preservedExpanded = new Set(
        Array.from(expandedGroups).filter(id => currentGroupIds.has(id))
      );
      setExpandedGroups(preservedExpanded);
    }
    
    // Update previous group IDs
    previousGroupIds.current = currentGroupIds;
  }, [fileGroups, expandedGroups]);

  // Ensure drag state is reset if drag ends outside the component
  useEffect(() => {
    const handleGlobalDragEnd = () => {
      if (draggedFile) {
        setDraggedFile(null);
        setDragOverFile(null);
        onDragEnd?.();
      }
    };

    if (draggedFile) {
      document.addEventListener('dragend', handleGlobalDragEnd);
      return () => {
        document.removeEventListener('dragend', handleGlobalDragEnd);
      };
    }
  }, [draggedFile, onDragEnd]);

  // Helper to check if click target is an interactive element
  const isInteractiveElement = (target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) return false;
    const element = target as HTMLElement;
    
    // Check if it's directly a button, link, input, etc.
    if (element.tagName === 'BUTTON' || element.tagName === 'A' || element.tagName === 'INPUT' || element.tagName === 'SELECT') {
      return true;
    }
    
    // Check if it's within an interactive element - use more specific selectors
    const closestButton = element.closest('button');
    if (closestButton) return true;
    
    const closestLink = element.closest('a');
    if (closestLink) return true;
    
    const closestInput = element.closest('input, select, textarea');
    if (closestInput) return true;
    
    // Check for data attributes
    const closestFileActions = element.closest('[data-file-actions]');
    if (closestFileActions) return true;
    
    const closestShareButton = element.closest('[data-share-button]');
    if (closestShareButton) return true;
    
    const closestStopProp = element.closest('[data-stop-propagation]');
    if (closestStopProp) return true;
    
    // Check if it's within an element with role="button"
    const closestRoleButton = element.closest('[role="button"]');
    if (closestRoleButton) return true;
    
    return false;
  };

  const handlePlay = (e: React.MouseEvent, file: AudioFile) => {
    e.preventDefault();
    e.stopPropagation();
    // If custom play handler is provided, use it
    if (onFilePlay) {
      onFilePlay(file);
      return;
    }
    // If same file is playing, toggle pause
    if (currentFile && currentFile.id === file.id && isPlaying) {
      stop();
      return;
    }
    // Play immediately - no delay needed, store handles the transition
    play(file);
  };

  // Use local play state if provided (for local players), otherwise use global store
  const getIsPlaying = (fileId: string) => {
    if (onFilePlay && localPlayingFileId !== undefined && localIsPlaying !== undefined) {
      return localPlayingFileId === fileId && localIsPlaying;
    }
    return currentFile?.id === fileId && isPlaying;
  };

  const handleDragStartForLink = (e: React.DragEvent, file: AudioFile) => {
    // Don't start drag if clicking on interactive elements
    if (isInteractiveElement(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.stopPropagation();
    setDraggedFile(file);
    onDragStart?.(file);
  };

  const handleDragEndForLink = (e?: React.DragEvent) => {
    // Always reset drag state, even if event is undefined
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDraggedFile(null);
    setDragOverFile(null);
    onDragEnd?.();
  };

  const handleDragOver = (e: React.DragEvent, file: AudioFile) => {
    if (!draggedFile || draggedFile.id === file.id) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverFile(file.id);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFile(null);
  };

  const handleDropForLink = async (e: React.DragEvent, targetFile: AudioFile) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFile(null);

    const currentDraggedFile = draggedFile;
    
    // Always reset drag state first
    setDraggedFile(null);
    handleDragEndForLink(e);

    if (!currentDraggedFile || currentDraggedFile.id === targetFile.id) {
      return;
    }

    // Don't link if already in same group
    if (currentDraggedFile.groupId && targetFile.groupId && currentDraggedFile.groupId === targetFile.groupId) {
      success('Files already linked', 'These files are already linked together');
      return;
    }

    try {
      // Use the same logic as LinkFilesModal
      const groupId = targetFile.groupId || `group-${Date.now()}`;
      
      const filesToUpdate = new Set<string>();
      filesToUpdate.add(targetFile.id);
      filesToUpdate.add(currentDraggedFile.id);

      // If dragged file is already in another group, move all files from that group
      if (currentDraggedFile.groupId && currentDraggedFile.groupId !== groupId) {
        allFiles.forEach(f => {
          if (f.groupId === currentDraggedFile.groupId) {
            filesToUpdate.add(f.id);
          }
        });
      }

      // Update all files with the same groupId
      for (const fileId of filesToUpdate) {
        await updateFile(fileId, { groupId });
      }

      success('Files linked', `${filesToUpdate.size} file(s) linked successfully`);
      // Refresh to update grouping after linking files
      onFileUpdate?.();
    } catch (err) {
      console.error('Failed to link files:', err);
      showError('Failed to link files', 'Please try again.');
    }
  };

  const handleToggleStar = (e: React.MouseEvent, file: AudioFile) => {
    e.stopPropagation();
    if (starringIds.has(file.id)) return;
    setStarringIds((prev) => new Set(prev).add(file.id));
    void toggleFileStar(file.id)
      .catch(() => showError('Failed to update star'))
      .finally(() => {
        setStarringIds((prev) => {
          const next = new Set(prev);
          next.delete(file.id);
          return next;
        });
      });
  };

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleDownload = async (file: AudioFile) => {
    try {
      const url = await getBlobUrl(file.id);
      if (!url) throw new Error('No URL');
      
      // Direct download
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = file.originalName || file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      success('Download started');
    } catch (err) {
      showError('Download failed');
    }
  };

  const handleBpmEdit = (e: React.MouseEvent, file: AudioFile) => {
    e.stopPropagation();
    setEditingBpm(file.id);
    setTempBpm(file.bpm?.toString() || '');
  };

  const handleBpmSave = async (file: AudioFile) => {
    try {
      const bpmValue = tempBpm ? parseInt(tempBpm) : null;
      await updateFile(file.id, { bpm: bpmValue as any });
      success('BPM updated');
      setEditingBpm(null);
      // No reload needed - store updates local state automatically
    } catch (err) {
      showError('Failed to update BPM');
    }
  };

  const handleKeyEdit = (e: React.MouseEvent, file: AudioFile) => {
    e.stopPropagation();
    setEditingKey(file.id);
    setTempKey(file.key || '');
  };

  const handleKeySave = async (file: AudioFile) => {
    try {
      await updateFile(file.id, { key: tempKey || null as any });
      success('Key updated');
      setEditingKey(null);
      // No reload needed - store updates local state automatically
    } catch (err) {
      showError('Failed to update key');
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort fileGroups based on current sort settings
  const sortedFileGroups = useMemo(() => {
    if (!sortColumn || !fileGroups || fileGroups.length === 0) return fileGroups;

    // Create array with original index for stable sorting
    const withIndex = fileGroups.map((group, index) => ({ group, index }));
    
    const sorted = withIndex.sort((a, b) => {
      const mainFileA = a.group.mainFile;
      const mainFileB = b.group.mainFile;
      
      // Safety check: ensure mainFile exists
      if (!mainFileA || !mainFileB) {
        return a.index - b.index; // Maintain original order if missing
      }
      
      let comparison = 0;

      switch (sortColumn) {
        case 'name': {
          const nameA = mainFileA.name || '';
          const nameB = mainFileB.name || '';
          comparison = nameA.localeCompare(nameB, undefined, { sensitivity: 'base', numeric: true });
          break;
        }
        case 'date': {
          const dateA = getFileCreatedAtMs(mainFileA);
          const dateB = getFileCreatedAtMs(mainFileB);
          // Handle invalid dates (NaN)
          if (isNaN(dateA) && isNaN(dateB)) {
            comparison = 0;
          } else if (isNaN(dateA)) {
            comparison = 1; // Put invalid dates at end (for asc) or start (for desc)
          } else if (isNaN(dateB)) {
            comparison = -1; // Put invalid dates at end (for asc) or start (for desc)
          } else {
            comparison = dateA - dateB;
          }
          break;
        }
        case 'size': {
          const sizeA = typeof mainFileA.size === 'number' && !isNaN(mainFileA.size) ? mainFileA.size : 0;
          const sizeB = typeof mainFileB.size === 'number' && !isNaN(mainFileB.size) ? mainFileB.size : 0;
          comparison = sizeA - sizeB;
          break;
        }
        case 'duration': {
          const durationA = typeof mainFileA.duration === 'number' && !isNaN(mainFileA.duration) ? mainFileA.duration : 0;
          const durationB = typeof mainFileB.duration === 'number' && !isNaN(mainFileB.duration) ? mainFileB.duration : 0;
          comparison = durationA - durationB;
          break;
        }
        case 'bpm': {
          const bpmA = typeof mainFileA.bpm === 'number' && !isNaN(mainFileA.bpm) ? mainFileA.bpm : 0;
          const bpmB = typeof mainFileB.bpm === 'number' && !isNaN(mainFileB.bpm) ? mainFileB.bpm : 0;
          comparison = bpmA - bpmB;
          break;
        }
        case 'key': {
          const keyA = mainFileA.key || '';
          const keyB = mainFileB.key || '';
          comparison = keyA.localeCompare(keyB, undefined, { sensitivity: 'base' });
          break;
        }
        default:
          comparison = 0;
      }

      // If values are equal, maintain original order (stable sort)
      if (comparison === 0) {
        return a.index - b.index;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    // Extract groups from sorted array
    return sorted.map(item => item.group);
  }, [fileGroups, sortColumn, sortDirection]);

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 inline-block text-primary" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 inline-block text-primary" />
    );
  };

  return (
    <div className="w-full bg-dark-secondary/40 backdrop-blur-sm border border-white/5 rounded-xl md:rounded-2xl shadow-lg overflow-hidden">
      {/* Mobile: Vertical List with Horizontal Rows - Ultra Compact - WITH SORT HEADER */}
      <div className="@explorer:hidden">
        {/* Mobile Sort Header */}
        <div className="flex items-center justify-between gap-2 p-2.5 bg-white/5 border-b border-white/10">
          <button
            onClick={() => handleSort('name')}
            className={cn(
              "flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg hover:bg-white/10 active:bg-white/15 transition-all text-xs font-semibold touch-manipulation min-h-[44px] flex-1",
              sortColumn === 'name' ? "bg-white/10 text-white" : "text-white/70"
            )}
          >
            Name
            <SortIcon column="name" />
          </button>
          <button
            onClick={() => handleSort('date')}
            className={cn(
              "flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg hover:bg-white/10 active:bg-white/15 transition-all text-xs font-semibold touch-manipulation min-h-[44px] flex-1",
              sortColumn === 'date' ? "bg-white/10 text-white" : "text-white/70"
            )}
          >
            Date
            <SortIcon column="date" />
          </button>
        </div>
        <div className="divide-y divide-white/5">
        {sortedFileGroups.map((group) => {
          const { mainFile, versions } = group;
          const displayMain = resolveFile(mainFile);
          const collaboratorLabel = getCollaboratorLabelForGroup(group);
          const hasStemsFolders = group.stemsFolders && group.stemsFolders.length > 0;
          const hasVersions = versions.length > 0 || hasStemsFolders;
          const isExpanded = expandedGroups.has(group.id);
          
          return (
            <div key={group.id}>
              {/* Main File Row - Mobile: Horizontal Layout, Desktop: Grid */}
              <div
            draggable
            onDragStart={(e) => handleDragStartForLink(e, mainFile)}
            onDragEnd={handleDragEndForLink}
            onDragOver={(e) => handleDragOver(e, mainFile)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDropForLink(e, mainFile)}
                onMouseDown={(e) => {
                  // Don't trigger if clicking on interactive elements (buttons, FileActions, etc.)
                  if (isInteractiveElement(e.target)) {
                    e.stopPropagation();
                    return;
                  }
                }}
                onClick={(e) => {
                  // Don't trigger if this was a drag operation
                  if (draggedFile) {
                    return;
                  }
                  // Don't trigger if clicking on expanded versions container or its children
                  const target = e.target as HTMLElement;
                  if (target.closest('[data-version-container]') || target.closest('[data-version-row]')) {
                    return;
                  }
                  // Don't trigger if clicking on interactive elements (buttons, FileActions, etc.)
                  if (isInteractiveElement(e.target)) {
                    e.stopPropagation();
                    return;
                  }
                  // Multi-select logic: If files are already selected, toggle select instead of opening
                  // This enables multi-select on mobile without needing Ctrl/Cmd
                  if (selectedFileIds.length > 0 || e.ctrlKey || e.metaKey) {
                    e.stopPropagation();
                    onToggleSelect?.(mainFile.id);
                  } else {
                    onFileClick?.(mainFile);
                  }
                }}
            className={cn(
                  "flex items-center gap-1.5 p-1.5 hover:bg-white/5 active:bg-white/10 transition-all duration-200 ease-out cursor-pointer group touch-manipulation min-h-[48px]",
                  draggedFile && draggedFile.id !== mainFile.id && "opacity-40",
                  mainFile.isShared && "border-l-2 border-primary/40",
                  selectedFileIds.includes(mainFile.id) && "bg-primary/10 border-l-2 border-l-primary",
                  draggedFile && draggedFile.id !== mainFile.id && "cursor-grab",
                  dragOverFile === mainFile.id && "bg-primary/10 ring-1 ring-primary/30 border-l-primary/60"
            )}
          >
                {/* Expand Icon for Versions - Compact */}
            <div className="flex items-center justify-center flex-shrink-0">
                  {hasVersions ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(group.id);
                      }}
                      className="p-0.5 hover:bg-white/10 rounded transition-all"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-3.5 h-3.5 text-primary" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-primary" />
                      )}
                    </button>
                  ) : (
              <div
                className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: FILE_TYPE_COLORS[mainFile.type] + '40' }}
              >
                <FileAudio
                  className="w-3.5 h-3.5"
                        style={{ color: FILE_TYPE_COLORS[mainFile.type] }}
                />
              </div>
                  )}
            </div>

                {/* Play Button - Compact */}
            <div className="flex items-center justify-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => handlePlay(e, mainFile)}
                className={cn(
                  "w-8 h-8 flex items-center justify-center rounded-md bg-gradient-to-r from-[#30d870]/80 to-[#00ff61]/80 hover:from-[#30d870] hover:to-[#00ff61] text-black transition-all hover:scale-110 active:scale-95 touch-manipulation",
                  currentFile?.id === mainFile.id && isPlaying && "from-[#30d870] to-[#00ff61] ring-2 ring-primary/50"
                )}
                aria-label={currentFile?.id === mainFile.id && isPlaying ? "Pause" : "Play"}
              >
                {currentFile?.id === mainFile.id && isPlaying ? (
                  <Pause className="w-4 h-4 fill-current" />
                ) : (
                  <Play className="w-4 h-4 fill-current ml-0.5" />
                )}
              </button>
            </div>

                {/* Star Button - Compact */}
            <div className="flex items-center justify-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => handleToggleStar(e, mainFile)}
                className="flex items-center justify-center p-1 hover:bg-white/10 rounded transition-all touch-manipulation"
                title={displayMain.isStarred ? 'Unstar' : 'Star'}
                aria-label={displayMain.isStarred ? 'Unstar file' : 'Star file'}
              >
                <Star 
                  className={cn(
                    "w-4 h-4 transition-all",
                    displayMain.isStarred 
                      ? "fill-yellow-400 text-yellow-400" 
                      : "text-white/40 hover:text-yellow-400"
                  )} 
                />
              </button>
            </div>

                {/* Name with Date and Metadata - Compact Horizontal */}
            <div className="flex flex-col min-w-0 flex-1 gap-0.5 overflow-hidden">
                  <span 
                    className="font-inter font-semibold text-sm text-white leading-tight line-clamp-1 truncate"
                    title={mainFile.name}
                  >
                    {mainFile.name}
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Date - Always visible on mobile - Compact format */}
                    <span className="text-[10px] font-inter text-white/50 whitespace-nowrap">
                      {new Date(mainFile.uploadedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                    </span>
                    {/* Show BPM and Key on mobile if available */}
                    {mainFile.bpm && (
                      <span className="text-[9px] font-codec font-bold text-primary px-1 py-0.5 bg-primary/20 rounded">
                        {formatBPM(mainFile.bpm)}
                      </span>
                    )}
                    {mainFile.key && (
                      <span className="text-[9px] font-codec font-bold text-beat px-1 py-0.5 bg-beat/20 rounded">
                        {mainFile.key}
                      </span>
                    )}
                    {collaboratorLabel && (
                      <span
                        className="hidden min-[380px]:inline text-[9px] font-inter text-white/50 truncate max-w-[140px]"
                        title={collaboratorLabel}
                      >
                        · with {collaboratorLabel}
                      </span>
                    )}
                  </div>
            </div>

                {/* Tags Column - XL screens only */}
                <div className="hidden xl:flex items-center justify-start gap-1 overflow-hidden flex-wrap">
                  {mainFile.tags && mainFile.tags.length > 0 ? (
                    <>
                      {mainFile.tags.slice(0, 3).map((tag, index) => (
                        <Tag key={index} tag={tag} className="text-xs whitespace-nowrap" />
                      ))}
                      {mainFile.tags.length > 3 && (
                        <span className="text-xs text-white/50 whitespace-nowrap">+{mainFile.tags.length - 3}</span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-white/40 italic">No tags</span>
                  )}
                </div>

                {/* Version Type Column - Always visible from sm */}
                <div className="hidden sm:flex items-center justify-center" />

            {/* BPM - Visible from small screens - Quick Edit */}
                <div className="hidden sm:flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                  {editingBpm === mainFile.id ? (
                    <input
                      type="number"
                      value={tempBpm}
                      onChange={(e) => setTempBpm(e.target.value)}
                      onBlur={() => handleBpmSave(mainFile)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleBpmSave(mainFile);
                        if (e.key === 'Escape') setEditingBpm(null);
                      }}
                      autoFocus
                      className="w-10 sm:w-12 md:w-14 px-1 py-0.5 bg-primary/30 text-primary rounded text-[10px] font-codec font-bold text-center focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="BPM"
                    />
                  ) : (
                    <button
                      onClick={(e) => handleBpmEdit(e, mainFile)}
                      className="px-1 py-0.5 hover:bg-primary/30 rounded transition-all group"
                    >
                      {mainFile.bpm ? (
                        <span className="text-[10px] font-codec font-bold text-primary">
                          {formatBPM(mainFile.bpm)}
                        </span>
                      ) : (
                        <span className="text-[10px] text-white/30 group-hover:text-primary">+</span>
                      )}
                    </button>
                  )}
            </div>

            {/* Key - Visible from small screens - Quick Edit */}
                <div className="hidden sm:flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                  {editingKey === mainFile.id ? (
                    <input
                      type="text"
                      value={tempKey}
                      onChange={(e) => setTempKey(e.target.value.toUpperCase())}
                      onBlur={() => handleKeySave(mainFile)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleKeySave(mainFile);
                        if (e.key === 'Escape') setEditingKey(null);
                      }}
                      autoFocus
                      maxLength={4}
                      className="w-10 sm:w-12 md:w-14 px-1 py-0.5 bg-beat/30 text-beat rounded text-[10px] font-codec font-bold text-center focus:outline-none focus:ring-1 focus:ring-beat"
                      placeholder="Key"
                    />
                  ) : (
                    <button
                      onClick={(e) => handleKeyEdit(e, mainFile)}
                      className="px-1 py-0.5 hover:bg-beat/30 rounded transition-all group"
                    >
                      {mainFile.key ? (
                        <span className="text-[10px] font-codec font-bold text-beat">
                          {mainFile.key}
                        </span>
                      ) : (
                        <span className="text-[10px] text-white/30 group-hover:text-beat">+</span>
                      )}
                    </button>
                  )}
            </div>

            {/* Duration - Visible from medium screens */}
                <div className="hidden @explorer:flex items-center justify-center font-inter text-[10px] text-white/60">
                  {formatDuration(mainFile.duration)}
            </div>

            {/* Size - Visible from medium screens */}
                <div className="hidden @explorer:flex items-center justify-center font-inter text-[10px] text-white/60">
                  {formatBytes(mainFile.size)}
            </div>

            {/* Actions - Compact */}
            {!hideActions && (
                <div 
                  className="flex items-center gap-1 flex-shrink-0" 
                  data-stop-propagation 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDownload(mainFile);
                    }}
                    className="min-w-[32px] min-h-[32px] flex items-center justify-center rounded-lg hover:bg-primary/10 active:bg-primary/20 text-white/60 hover:text-primary transition-all touch-manipulation group"
                    title="Download file"
                    aria-label="Download"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                    <ShareButton
                      file={mainFile}
                      onSuccess={() => onFileUpdate?.()}
                      size="sm"
                      variant="icon"
                    />
                  </div>
                  <div 
                    className="flex items-center justify-center touch-manipulation min-w-[32px] min-h-[32px]"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <FileActions
                      file={mainFile}
                      onRename={() => setRenameFile(mainFile)}
                      onDelete={() => onFileUpdate?.()}
                      onMove={() => onFileUpdate?.()}
                    />
                  </div>
                  {onViewGroupInVersions && (
                    <ManageVersionsButton
                      hasVersions={hasVersions}
                      onClick={() => onViewGroupInVersions(group.id)}
                    />
                  )}
                </div>
            )}
            </div>

              {/* Expanded Versions - Mobile */}
              {isExpanded && hasVersions && (
                <div className="bg-white/5 border-l-2 border-primary/30 pl-2" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} onMouseDown={(e) => { e.stopPropagation(); }} data-version-container>
                  {versions.map((version) => (
                    <div
                      key={version.id}
                      data-version-row
                      draggable
                      onDragStart={(e) => handleDragStartForLink(e, version)}
                      onDragEnd={handleDragEndForLink}
                      onDragOver={(e) => handleDragOver(e, version)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDropForLink(e, version)}
                      onMouseDown={(e) => { e.stopPropagation(); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        isClickingVersion.current = true;
                        // Multi-select logic for versions too
                        if (selectedFileIds.length > 0 || e.ctrlKey || e.metaKey) {
                          onToggleSelect?.(version.id);
                        } else {
                          onFileClick?.(version);
                        }
                      }}
                      className={cn(
                        "flex items-center gap-1.5 p-1.5 hover:bg-white/8 transition-all duration-200 ease-out cursor-grab active:cursor-grabbing",
                        _isDragging && "opacity-40",
                        draggedFile && draggedFile.id !== version.id && "cursor-grab",
                        dragOverFile === version.id && "bg-primary/10 ring-1 ring-primary/30",
                        selectedFileIds.includes(version.id) && "bg-primary/10"
                      )}
                    >
                      <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 opacity-60" style={{ backgroundColor: FILE_TYPE_COLORS[version.type] + '40' }}>
                        <FileAudio className="w-3 h-3" style={{ color: FILE_TYPE_COLORS[version.type] }} />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                        <span className="font-inter font-semibold text-xs text-white line-clamp-1">{version.name}</span>
                        <span className="text-[10px] font-inter text-white/50">{new Date(version.uploadedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                      </div>
                    </div>
                  ))}
                  {/* Stems Folders */}
                  {group.stemsFolders && group.stemsFolders.map((folder) => (
                    <div
                      key={folder.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onFolderClick?.(folder);
                      }}
                      className="flex items-center gap-1.5 p-1.5 hover:bg-primary/10 transition-all cursor-pointer bg-primary/5 border-l-2 border-primary/40"
                    >
                      <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 bg-primary/30">
                        <FolderIcon className="w-3 h-3 text-primary" />
                      </div>
                      <span className="font-inter font-semibold text-xs text-white line-clamp-1 flex-1">{folder.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>

      {/* Desktop: Table Header + Table Body */}
      <div className="hidden @explorer:block min-w-0">
        {/* Desktop Table Header */}
        <div className="grid grid-cols-[35px_40px_35px_minmax(150px,1fr)_0px_80px_45px_50px_50px_60px_185px] lg:grid-cols-[35px_40px_35px_minmax(180px,1.5fr)_0px_90px_50px_55px_55px_70px_195px] xl:grid-cols-[35px_40px_35px_minmax(200px,2fr)_150px_100px_50px_55px_60px_75px_200px] gap-1.5 lg:gap-2 xl:gap-2.5 p-2 lg:p-2.5 xl:p-3 pr-6 lg:pr-8 xl:pr-10 bg-white/5 font-codec font-bold text-xs text-white/80">
          <div></div>
          <div className="text-center">Play</div>
          <div className="text-center">Star</div>
          <button
            onClick={() => handleSort('name')}
            className="text-left hover:text-white transition-colors cursor-pointer flex items-center"
          >
            Name
            <SortIcon column="name" />
          </button>
          <div className="hidden xl:block">Tags</div>
          <div className="text-center">Version</div>
          <button
            onClick={() => handleSort('bpm')}
            className="text-center hover:text-white transition-colors cursor-pointer flex items-center justify-center"
          >
            BPM
            <SortIcon column="bpm" />
          </button>
          <button
            onClick={() => handleSort('key')}
            className="text-center hover:text-white transition-colors cursor-pointer flex items-center justify-center"
          >
            Key
            <SortIcon column="key" />
          </button>
          <button
            onClick={() => handleSort('duration')}
            className="hidden @explorer:block text-center hover:text-white transition-colors cursor-pointer flex items-center justify-center"
          >
            Duration
            <SortIcon column="duration" />
          </button>
          <button
            onClick={() => handleSort('size')}
            className="hidden @explorer:block text-center hover:text-white transition-colors cursor-pointer flex items-center justify-center"
          >
            Size
            <SortIcon column="size" />
          </button>
          <button
            onClick={() => handleSort('date')}
            className="hidden @explorer:block text-center hover:text-white transition-colors cursor-pointer flex items-center justify-center whitespace-nowrap"
          >
            {hideActions ? 'Date' : 'Date & Actions'}
            <SortIcon column="date" />
          </button>
        </div>
        {/* Desktop Table Body */}
        <div className="divide-y divide-white/5">
        {sortedFileGroups.map((group) => {
          const { mainFile, versions } = group;
          const displayMain = resolveFile(mainFile);
          const collaboratorLabel = getCollaboratorLabelForGroup(group);
          const hasStemsFolders = group.stemsFolders && group.stemsFolders.length > 0;
          const hasVersions = versions.length > 0 || hasStemsFolders;
          const isExpanded = expandedGroups.has(group.id);
          
          return (
            <div key={group.id}>
              {/* Main File Row - Desktop Grid */}
              <div
            draggable
            onDragStart={(e) => handleDragStartForLink(e, mainFile)}
            onDragEnd={handleDragEndForLink}
            onDragOver={(e) => handleDragOver(e, mainFile)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDropForLink(e, mainFile)}
                onMouseDown={(e) => {
                  // Don't trigger if clicking on interactive elements (buttons, FileActions, etc.)
                  if (isInteractiveElement(e.target)) {
                    e.stopPropagation();
                    return;
                  }
                }}
                onClick={(e) => {
                  // Don't trigger if this was a drag operation
                  if (draggedFile) {
                    return;
                  }
                  // Don't trigger if clicking on expanded versions container or its children
                  const target = e.target as HTMLElement;
                  if (target.closest('[data-version-container]') || target.closest('[data-version-row]')) {
                    return;
                  }
                  // Don't trigger if clicking on interactive elements (buttons, FileActions, etc.)
                  if (isInteractiveElement(e.target)) {
                    e.stopPropagation();
                    return;
                  }
                  // Multi-select logic: If files are already selected, toggle select instead of opening
                  if (selectedFileIds.length > 0 || e.ctrlKey || e.metaKey) {
                    e.stopPropagation();
                    onToggleSelect?.(mainFile.id);
                  } else {
                    onFileClick?.(mainFile);
                  }
                }}
            className={cn(
                  "grid grid-cols-[35px_40px_35px_minmax(150px,1fr)_0px_80px_45px_50px_50px_60px_185px] lg:grid-cols-[35px_40px_35px_minmax(180px,1.5fr)_0px_90px_50px_55px_55px_70px_195px] xl:grid-cols-[35px_40px_35px_minmax(200px,2fr)_150px_100px_50px_55px_60px_75px_200px] gap-1.5 lg:gap-2 xl:gap-2.5 p-2.5 lg:p-2.5 xl:p-3 pr-6 lg:pr-8 xl:pr-10 hover:bg-white/5 transition-all duration-200 ease-out cursor-pointer group touch-manipulation items-center",
                  draggedFile && draggedFile.id !== mainFile.id && "opacity-40",
                  mainFile.isShared && "border-l-2 border-primary/40",
                  selectedFileIds.includes(mainFile.id) && "bg-primary/10 border-l-2 border-l-primary",
                  draggedFile && draggedFile.id !== mainFile.id && "cursor-grab",
                  dragOverFile === mainFile.id && "bg-primary/10 ring-1 ring-primary/30 border-l-primary/60"
            )}
          >
                {/* Col 1: File Icon (always visible) */}
            <div className="flex items-center justify-center">
              <div
                className="w-8 h-8 rounded flex items-center justify-center"
                style={{ backgroundColor: FILE_TYPE_COLORS[mainFile.type] + '40' }}
              >
                <FileAudio
                  className="w-5 h-5"
                  style={{ color: FILE_TYPE_COLORS[mainFile.type] }}
                />
              </div>
            </div>

                {/* Play Button */}
            <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => handlePlay(e, mainFile)}
                className={cn(
                  "w-8 h-8 flex items-center justify-center rounded-md bg-gradient-to-r from-[#30d870]/80 to-[#00ff61]/80 hover:from-[#30d870] hover:to-[#00ff61] text-black transition-all hover:scale-110 active:scale-95",
                  getIsPlaying(mainFile.id) && "ring-2 ring-primary/50"
                )}
                aria-label={getIsPlaying(mainFile.id) ? "Pause" : "Play"}
              >
                {getIsPlaying(mainFile.id) ? (
                  <Pause className="w-4 h-4 fill-current" />
                ) : (
                  <Play className="w-4 h-4 fill-current ml-0.5" />
                )}
              </button>
            </div>

                {/* Star Button */}
            <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => handleToggleStar(e, mainFile)}
                className="flex items-center justify-center p-1 hover:bg-white/10 rounded transition-all"
                title={displayMain.isStarred ? 'Unstar' : 'Star'}
                aria-label={displayMain.isStarred ? 'Unstar file' : 'Star file'}
              >
                <Star 
                  className={cn(
                    "w-5 h-5 transition-all",
                    displayMain.isStarred 
                      ? "fill-yellow-400 text-yellow-400" 
                      : "text-white/40 hover:text-yellow-400"
                  )} 
                />
              </button>
            </div>

                {/* Name with Status */}
            <div className="flex flex-col items-start justify-center min-w-0 flex-1">
                  <span 
                    className="font-inter font-semibold text-xs text-white leading-tight line-clamp-1 truncate w-full"
                    title={mainFile.name}
                  >
                    {mainFile.name}
                  </span>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <StatusBadge status={mainFile.status} size="sm" />
                    {collaboratorLabel && (
                      <span className="text-xs text-white/50 font-inter truncate max-w-[120px] sm:max-w-[180px]" title={collaboratorLabel}>
                        · with {collaboratorLabel}
                      </span>
                    )}
                  </div>
            </div>

                {/* Tags Column */}
                <div className="hidden xl:flex items-center justify-start gap-1 overflow-hidden flex-wrap">
                  {mainFile.tags && mainFile.tags.length > 0 ? (
                    <>
                      {mainFile.tags.slice(0, 3).map((tag, index) => (
                        <Tag key={index} tag={tag} className="text-xs whitespace-nowrap" />
                      ))}
                      {mainFile.tags.length > 3 && (
                        <span className="text-xs text-white/50 whitespace-nowrap">+{mainFile.tags.length - 3}</span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-white/40 italic">No tags</span>
                  )}
                </div>

                {/* Col 6: Version Buttons (Expand + Manage versions) */}
                <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                  {hasVersions && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(group.id);
                      }}
                      className="p-1 hover:bg-white/10 rounded transition-all"
                      title={isExpanded ? 'Collapse versions' : 'Expand versions'}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-primary" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-primary" />
                      )}
                    </button>
                  )}
                  {onViewGroupInVersions && (
                    <ManageVersionsButton
                      hasVersions={hasVersions}
                      onClick={() => onViewGroupInVersions(group.id)}
                    />
                  )}
                </div>

            {/* BPM */}
                <div className="hidden sm:flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                  {editingBpm === mainFile.id ? (
                    <input
                      type="number"
                      value={tempBpm}
                      onChange={(e) => setTempBpm(e.target.value)}
                      onBlur={() => handleBpmSave(mainFile)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleBpmSave(mainFile);
                        if (e.key === 'Escape') setEditingBpm(null);
                      }}
                      autoFocus
                      className="w-10 sm:w-12 md:w-14 px-1 py-0.5 bg-primary/30 text-primary rounded text-[10px] font-codec font-bold text-center focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="BPM"
                    />
                  ) : (
                    <button
                      onClick={(e) => handleBpmEdit(e, mainFile)}
                      className="px-1 py-0.5 hover:bg-primary/30 rounded transition-all group"
                    >
                      {mainFile.bpm ? (
                        <span className="text-[10px] font-codec font-bold text-primary">
                          {formatBPM(mainFile.bpm)}
                        </span>
                      ) : (
                        <span className="text-[10px] text-white/30 group-hover:text-primary">+</span>
                      )}
                    </button>
                  )}
            </div>

            {/* Key */}
                <div className="hidden sm:flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                  {editingKey === mainFile.id ? (
                    <input
                      type="text"
                      value={tempKey}
                      onChange={(e) => setTempKey(e.target.value.toUpperCase())}
                      onBlur={() => handleKeySave(mainFile)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleKeySave(mainFile);
                        if (e.key === 'Escape') setEditingKey(null);
                      }}
                      autoFocus
                      maxLength={4}
                      className="w-10 sm:w-12 md:w-14 px-1 py-0.5 bg-beat/30 text-beat rounded text-[10px] font-codec font-bold text-center focus:outline-none focus:ring-1 focus:ring-beat"
                      placeholder="Key"
                    />
                  ) : (
                    <button
                      onClick={(e) => handleKeyEdit(e, mainFile)}
                      className="px-1 py-0.5 hover:bg-beat/30 rounded transition-all group"
                    >
                      {mainFile.key ? (
                        <span className="text-[10px] font-codec font-bold text-beat">
                          {mainFile.key}
                        </span>
                      ) : (
                        <span className="text-[10px] text-white/30 group-hover:text-beat">+</span>
                      )}
                    </button>
                  )}
            </div>

            {/* Duration */}
                <div className="hidden @explorer:flex items-center justify-center font-inter text-[10px] text-white/60 min-w-0 truncate whitespace-nowrap" title={formatDuration(mainFile.duration)}>
                  {formatDuration(mainFile.duration)}
            </div>

            {/* Size */}
                <div className="hidden @explorer:flex items-center justify-center font-inter text-[10px] text-white/60 min-w-0 truncate whitespace-nowrap" title={formatBytes(mainFile.size)}>
                  {formatBytes(mainFile.size)}
            </div>

            {/* Date & Actions */}
                <div 
                  className="flex items-center justify-center gap-0.5 relative" 
                  data-stop-propagation 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <span 
                    className="font-inter text-[10px] text-white/60 whitespace-nowrap" 
                    title={formatDate(mainFile.uploadedAt)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {formatDate(mainFile.uploadedAt)}
                  </span>
                  {!hideActions && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDownload(mainFile);
                        }}
                        className="min-w-[32px] min-h-[32px] flex items-center justify-center rounded-lg hover:bg-primary/10 active:bg-primary/20 text-white/60 hover:text-primary transition-all group"
                        title="Download file"
                        aria-label="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <ShareButton
                          file={mainFile}
                          onSuccess={() => onFileUpdate?.()}
                          size="md"
                          variant="icon"
                        />
                      </div>
                      <div 
                        className="flex items-center justify-center min-w-[32px] min-h-[32px]"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <FileActions
                          file={mainFile}
                          onRename={() => setRenameFile(mainFile)}
                          onDelete={() => onFileUpdate?.()}
                          onMove={() => onFileUpdate?.()}
                        />
                      </div>
                    </>
                  )}
            </div>
            </div>

              {/* Expanded Versions - Desktop */}
              {isExpanded && hasVersions && (
                <div className="bg-white/5 border-l-2 border-primary/30" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} onMouseDown={(e) => { e.stopPropagation(); }} data-version-container>
                  {versions.map((version) => (
                    <div
                      key={version.id}
                      data-version-row
                      draggable
                      onDragStart={(e) => handleDragStartForLink(e, version)}
                      onDragEnd={handleDragEndForLink}
                      onDragOver={(e) => handleDragOver(e, version)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDropForLink(e, version)}
                      onMouseDown={(e) => { e.stopPropagation(); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        isClickingVersion.current = true;
                        // Ctrl/Cmd+Click for multi-select (auch für Versionen!)
                        if (selectedFileIds.length > 0 || e.ctrlKey || e.metaKey) {
                          onToggleSelect?.(version.id);
                        } else {
                          onFileClick?.(version);
                        }
                      }}
                      className={cn("grid grid-cols-[35px_40px_35px_minmax(150px,1fr)_0px_80px_45px_50px_50px_60px_185px] lg:grid-cols-[35px_40px_35px_minmax(180px,1.5fr)_0px_90px_50px_55px_55px_70px_195px] xl:grid-cols-[35px_40px_35px_minmax(200px,2fr)_150px_100px_50px_55px_60px_75px_200px] gap-1.5 lg:gap-2 xl:gap-2.5 p-1.5 md:p-2 xl:p-2.5 pr-6 lg:pr-8 xl:pr-10 hover:bg-white/8 transition-all duration-200 ease-out cursor-grab active:cursor-grabbing items-center",
                        _isDragging && "opacity-40",
                        draggedFile && draggedFile.id !== version.id && "cursor-grab",
                        dragOverFile === version.id && "bg-primary/10 ring-1 ring-primary/30",
                        selectedFileIds.includes(version.id) && "bg-primary/10 border-l-4 border-primary"
                      )}
                    >
                      {/* Col 1: Version Icon */}
                      <div className="flex items-center justify-center opacity-60">
                        <div
                          className="w-5 h-5 rounded flex items-center justify-center"
                          style={{ backgroundColor: FILE_TYPE_COLORS[version.type] + '40' }}
                        >
                          <FileAudio
                            className="w-3 h-3"
                            style={{ color: FILE_TYPE_COLORS[version.type] }}
                          />
                        </div>
                      </div>

                      {/* Col 2: Empty (no play button for versions) */}
                      <div />

                      {/* Col 3: Star Button */}
                      <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => handleToggleStar(e, version)}
                          className="flex items-center justify-center p-0.5 hover:bg-white/10 rounded transition-all"
                          title={resolveFile(version).isStarred ? 'Unstar' : 'Star'}
                        >
                          <Star 
                            className={cn(
                              "w-3.5 h-3.5 transition-all",
                              resolveFile(version).isStarred 
                                ? "fill-yellow-400 text-yellow-400" 
                                : "text-white/40 hover:text-yellow-400"
                            )} 
                          />
                        </button>
                      </div>

                      {/* Col 4: Version Name (indented to signal sub-item) */}
                      <div className="flex items-center gap-2 min-w-0 pl-3">
                        <span 
                          className="font-inter font-semibold text-xs text-white leading-tight truncate"
                          title={version.name}
                        >
                          {version.name}
                        </span>
                        <StatusBadge status={version.status} size="xs" />
                      </div>

                      {/* Col 5: Tags (empty for versions, hidden xl) */}
                      <div className="hidden xl:block" />

                      {/* Col 6: Version Type (empty) */}
                      <div />

                      {/* Col 7: BPM (empty) */}
                      <div />

                      {/* Col 8: Key (empty) */}
                      <div />

                      {/* Col 9: Duration */}
                      <div className="flex items-center justify-center font-inter text-[10px] text-white/40 min-w-0 truncate whitespace-nowrap" title={formatDuration(version.duration)}>
                        {formatDuration(version.duration)}
                      </div>

                      {/* Col 10: Size */}
                      <div className="flex items-center justify-center font-inter text-[10px] text-white/40 min-w-0 truncate whitespace-nowrap" title={formatBytes(version.size)}>
                        {formatBytes(version.size)}
                      </div>

                      {/* Col 11: Date + Actions */}
                      <div className="flex items-center justify-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                        <span className="font-inter text-[10px] text-white/40 whitespace-nowrap" title={formatDate(version.uploadedAt)}>
                          {formatDate(version.uploadedAt)}
                        </span>
                        <button
                          onClick={() => handleDownload(version)}
                          className="min-w-[28px] min-h-[28px] flex items-center justify-center rounded-lg hover:bg-primary/10 text-white/40 hover:text-primary transition-all"
                          title="Download"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <ShareButton
                          file={version}
                          onSuccess={() => onFileUpdate?.()}
                          size="sm"
                          variant="icon"
                        />
                        <FileActions
                          file={version}
                          onRename={() => setRenameFile(version)}
                          onDelete={() => onFileUpdate?.()}
                          onMove={() => onFileUpdate?.()}
                        />
                      </div>
          </div>
        ))}
                  {/* Stems Folders - Desktop */}
                  {group.stemsFolders && group.stemsFolders.map((folder) => (
                    <div
                      key={folder.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onFolderClick?.(folder);
                      }}
                      className="grid grid-cols-[30px_28px_minmax(150px,1fr)_45px_45px] sm:grid-cols-[30px_28px_minmax(150px,1fr)_45px_45px_40px_35px_35px] md:grid-cols-[35px_35px_minmax(180px,1.5fr)_60px_80px_50px_70px_70px_35px_35px] lg:grid-cols-[35px_35px_minmax(200px,2fr)_70px_90px_55px_80px_80px_85px_40px_40px] xl:grid-cols-[35px_35px_minmax(220px,2.5fr)_150px_100px_70px_55px_75px_75px_90px_40px_40px] gap-1 sm:gap-1.5 md:gap-2 lg:gap-2 xl:gap-2.5 p-2 sm:p-2.5 md:p-3 lg:p-3 xl:p-3.5 pl-5 sm:pl-6 md:pl-10 hover:bg-primary/10 transition-all cursor-pointer items-center bg-primary/5 border-l-4 border-primary/40"
                    >
                      {/* Folder Icon */}
                      <div className="flex items-center justify-center">
                        <div className="w-6 h-6 rounded flex items-center justify-center bg-primary/30">
                          <FolderIcon className="w-4 h-4 text-primary" />
                        </div>
                      </div>
                      
                      {/* Empty space for star column */}
                      <div></div>
                      
                      {/* Folder Name */}
                      <div className="flex items-center gap-2">
                        <span className="font-inter font-semibold text-sm text-white line-clamp-1">{folder.name}</span>
                      </div>
                      
                      {/* Empty columns for alignment */}
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>

      {/* Rename Modal */}
      {renameFile && (
        <RenameModal
          file={renameFile}
          onClose={() => setRenameFile(null)}
          onSuccess={() => {
            onFileUpdate?.();
            setRenameFile(null);
          }}
        />
      )}
    </div>
  );
};

